import React from 'react';
import { Box, Chip, makeStyles, Theme } from '@material-ui/core';
import { ColumnFilter } from '../DataGridFilterMenu/DataGridFilterTypes';
import { ColumnInstance, Filters } from 'react-table';
import { format } from 'date-fns';
import { isIsoDateString } from '../utils';

export type DataGridAppliedFiltersProps = {
  columns?: ColumnInstance[];
  columnFilters?: ColumnFilter[];
  onDelete?: (filters: Filters<ColumnFilter>) => void;
  disabled?: false;
  color?: 'secondary';
  size?: 'small';
  variant?: 'default';
  clickable?: true;
};

export const formatDate = (date: Date | string): string => (date ? format(new Date(date), 'MMM dd yyyy') : date);

const useStyles = makeStyles((theme: Theme) => ({
  rootChips: {
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    '&:not(:empty)': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  chip: {
    marginRight: theme.spacing(1),
    maxWidth: '100%',
  },
  chipContainer: {
    paddingTop: theme.spacing(1),
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
    maxWidth: '100%',
  },
}));

const activeColumn = (columns: ColumnInstance[], columnId?: string | null): ColumnInstance | undefined => {
  return columns?.find((column) => {
    return column.id === columnId;
  });
};

// Temp implementaion
// This will be refactored in this ticket: 1310592
const labelValueMap = new Map([
  ['Invitation_Expired', 'Invitation Expired'],
  ['On-Hold', 'On Hold'],
]);

const getLabelValue = (labelValue: string | number | null) => {
  if (typeof labelValue !== 'string') {
    return labelValue;
  }

  // get sanitized label value if in map. Otherwise, default to current value
  return labelValueMap.get(labelValue) ?? labelValue;
};

export const DataGridAppliedFilters = ({
  columns,
  columnFilters = [],
  onDelete = () => undefined,
  ...args
}: DataGridAppliedFiltersProps): JSX.Element => {
  const classes = useStyles();
  const labelMap = new Map([
    ['is', 'is'],
    ['isNot', 'is not'],
    ['isEmpty', 'is empty'],
    ['isNotEmpty', 'is not empty'],
    ['isExactly', 'is exactly'],
    ['isGreaterThan', 'is greater than'],
    ['isLessThan', 'is less than'],
    ['isAfter', 'is after'],
    ['isBefore', 'is before'],
    ['isBetween', 'is between'],
    ['contains', 'contains'],
    ['notContains', 'does not contain'],
    ['startsWith', 'starts with'],
    ['includes', 'includes'],
    ['excludes', 'excludes'],
    ['equals', 'equals'],
    ['notEquals', 'not equals'],
    ['greaterThan', 'greater than'],
    ['greaterThanOrEqual', 'greater than or equal'],
    ['lessThan', 'less than'],
    ['lessThanOrEqual', 'less than or equal'],
    ['all', 'All'],
  ]);

  return (
    <Box className={classes.rootChips} data-testid="data-grid-applied-filter">
      {columnFilters.map((columnFilter) => {
        return columnFilter?.value?.map((filter) => {
          const getLabel = (val: string | Date | number | null | string[]) => {
            if (Array.isArray(val)) {
              val = val.join(', ');
            }
            if (val === 'MASTER') {
              val = 'Master';
            }
            return val instanceof Date || (typeof val === 'string' && isIsoDateString(val))
              ? `${activeColumn((columns as unknown) as ColumnInstance[], filter?.columnId)?.Header}: ${labelMap.get(
                  filter?.operator as string,
                )} ${formatDate(val)}`
              : `${activeColumn((columns as unknown) as ColumnInstance[], filter?.columnId)?.Header}: ${labelMap.get(
                  filter?.operator as string,
                )} ${getLabelValue(val)}`;
          };
          return (
            <div key={filter.columnId} className={classes.chipContainer}>
              <Chip
                data-testid={`filter-chip`}
                label={`${getLabel(filter.value)}`}
                onDelete={() => {
                  const tmpColumnFilters = [...columnFilters];
                  const idx = columnFilter?.value?.findIndex((filterObj) => filterObj.value === filter?.value);
                  if (idx !== -1 && idx !== undefined) columnFilter?.value?.splice(idx, 1);
                  if (columnFilter?.value?.length === 0) {
                    const indx = tmpColumnFilters?.findIndex((filterObj) => filterObj.id === filter?.columnId);
                    if (indx !== -1 && indx !== undefined) {
                      tmpColumnFilters?.splice(indx, 1);
                    }
                  }
                  onDelete(tmpColumnFilters as Filters<ColumnFilter>);
                }}
                className={classes.chip}
                size="small"
                {...args}
              />
            </div>
          );
        });
      })}
    </Box>
  );
};

export default DataGridAppliedFilters;
